import React, { useEffect, useState } from 'react';
import CustomLoading from '../components/customLoading/CustomLoading';
import Table from '../components/tables/Table';
import Breadcrumbs from '../components/BreadCrumbs';
import Pagination from '../components/tables/pagination/Pagination';
import Button from '../components/buttons/Button';
import { Link, useSearchParams } from 'react-router-dom';
import { statesOfIndia } from '../constants/states';
import SearchBar from '../components/searchBar/SearchBar';
import Dropdown from '../components/inputFields/DropDown';
import { BiRefresh, BiUserPlus } from 'react-icons/bi';
import { toast } from 'react-toastify';
import { getUsers } from '../services/farmers';
import ShowModal from '../utils/ShowModal';
import UserDetailsPop from './UserDetailsPop';
import DateRangePicker from '../components/inputFields/DateRangePicker';
import { History } from 'lucide-react';
import UserFeedbackPop from './UserFeedbackPop';
import MultiSelectDropdown from '../components/inputFields/MultiSelectDropDown';
import { getCropsMasterDataNoUser } from '../services/cropService';

function Farmers({ userType = "Farmers" }) {
    const [searchParams, setSearchParams] = useSearchParams();

    const [cardData, setCardData] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [filteredState, setFilteredState] = useState("");
    const [filterDate, setFilterDate] = useState({ start: null, end: null });
    const [filteredUserType, setFilteredUserType] = useState("");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const headers = [
        { label: "UID", key: "id", },
        { label: "Name", key: "name" },
        { label: "User Type", key: "user_type" },
        { label: "Phone", key: "mobile_number" },
        { label: "Commodities", key: "commodities" },
        { label: "State", key: "state" },
        { label: "Total Purchased", key: "total_purchased_amount", needFilter: true },
        { label: "Farm Size", key: "total_farm_size_acres", needFilter: true },
        { label: "Devices", key: "total_devices", needFilter: true },
        { label: "Farms", key: "total_registered_farms", needFilter: true },
        { label: "Satt. Farms", key: "total_satellite_registered_farms", needFilter: true },
        { label: "Feedback", key: "feedback" },
    ];

    const [selectedSort, setSelectedSort] = useState("");
    const [selectedSortDir, setSelectedSortDir] = useState("");

    // Helper function to update URL parameters
    const updateURLParams = (updates) => {
        const newParams = new URLSearchParams(searchParams);

        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "" ||
                (Array.isArray(value) && value.length === 0) ||
                (typeof value === 'object' && value.start === null && value.end === null)) {
                newParams.delete(key);
            } else if (Array.isArray(value)) {
                newParams.set(key, value.join(','));
            } else if (typeof value === 'object' && value.start !== undefined && value.end !== undefined) {
                // Handle date range object properly
                if (value.start && value.end) {
                    newParams.set(key, `${value.start},${value.end}`);
                } else {
                    newParams.delete(key);
                }
            } else {
                newParams.set(key, value);
            }
        });

        setSearchParams(newParams, { replace: true });
    };

    // Helper function to get URL parameters
    const getURLParams = () => {
        const params = {};

        // Get search text
        params.search = searchParams.get('search') || '';

        // Get state filter
        params.state = searchParams.get('state') || '';

        // Get user type filter
        params.userType = searchParams.get('userType') || '';

        // Get date range
        const dateRange = searchParams.get('dateRange');
        if (dateRange && dateRange !== '[object Object]') {
            const [start, end] = dateRange.split(',');
            // Validate that the dates are valid
            const startDate = start && start !== 'null' ? start : null;
            const endDate = end && end !== 'null' ? end : null;
            
            // Additional validation to ensure dates are in correct format
            if (startDate && endDate) {
                const startDateObj = new Date(startDate);
                const endDateObj = new Date(endDate);
                if (!isNaN(startDateObj.getTime()) && !isNaN(endDateObj.getTime())) {
                    params.dateRange = { start: startDate, end: endDate };
                } else {
                    params.dateRange = { start: null, end: null };
                }
            } else {
                params.dateRange = { start: null, end: null };
            }
        } else {
            params.dateRange = { start: null, end: null };
        }

        // Get farm area
        params.farmArea = searchParams.get('farmArea') || '-';

        // Get commodities
        const commodities = searchParams.get('commodities');
        params.commodities = commodities ? commodities.split(',') : [];

        // Get sort parameters
        params.sortBy = searchParams.get('sortBy') || '';
        params.sortOrder = searchParams.get('sortOrder') || '';

        // Get current page
        params.page = parseInt(searchParams.get('page')) || 1;

        return params;
    };

    const handleTableSort = (sortData) => {
        if (sortData.filter !== 'none') {
            setSelectedSort(sortData.key);
            setSelectedSortDir(sortData.filter);
            updateURLParams({
                sortBy: sortData.key,
                sortOrder: sortData.filter,
                page: 1
            });
            const commodityQuery = buildCommodityQuery(filteredCommodities);
            fetchData(`username=${searchText}&start_date=${filterDate.start || ""}&end_date=${filterDate.end || ""}&state=${filteredState}&user_type=${filteredUserType}&min_farm_size=${filteredFarmArea.split("-")[0]}&max_farm_size=${filteredFarmArea.split("-")[1]}&${commodityQuery}&sort_by=${sortData.key}&sort_order=${sortData.filter}&page=1`);
        } else {
            setSelectedSort("");
            setSelectedSortDir("");
            updateURLParams({
                sortBy: "",
                sortOrder: "",
                page: 1
            });
            const commodityQuery = buildCommodityQuery(filteredCommodities);
            fetchData(`username=${searchText}&start_date=${filterDate.start || ""}&end_date=${filterDate.end || ""}&state=${filteredState}&user_type=${filteredUserType}&min_farm_size=${filteredFarmArea.split("-")[0]}&max_farm_size=${filteredFarmArea.split("-")[1]}&${commodityQuery}&page=1`);
        }
    };


    const [commodities, setCommodities] = useState([]);

    const [modalIsOpen, setIsOpen] = useState(false);
    const [openingItem, setOpeningItem] = useState({});
    const [feedbackModalIsOpen, setFeedbackModalIsOpen] = useState(false);
    const [feedbackItem, setFeedbackItem] = useState({});
    const [filteredFarmArea, setFilteredFarmArea] = useState("-");
    const [filteredCommodities, setFilteredCommodities] = useState([]);
    const pageSize = 100;

    const loadCommodityData = async () => {
        try {
            const cropsData = await getCropsMasterDataNoUser();
            setCommodities(cropsData.data || []);
        } catch (error) {
            console.error("Error loading initial data:", error);
        }
    };

    useEffect(() => {
        loadCommodityData();
    }, []);

    // One-time cleanup for invalid URL parameters
    useEffect(() => {
        const currentDateRange = searchParams.get('dateRange');
        if (currentDateRange === '[object Object]' || (currentDateRange && currentDateRange.includes('[object Object]'))) {
            // Clear the invalid dateRange parameter
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('dateRange');
            setSearchParams(newParams, { replace: true });
        }
    }, []); // Run only once on mount

    // Initialize state from URL parameters
    useEffect(() => {
        const urlParams = getURLParams();

        setSearchText(urlParams.search);
        setFilteredState(urlParams.state);
        setFilteredUserType(urlParams.userType);
        setFilterDate(urlParams.dateRange);
        setFilteredFarmArea(urlParams.farmArea);
        setFilteredCommodities(urlParams.commodities);
        setSelectedSort(urlParams.sortBy);
        setSelectedSortDir(urlParams.sortOrder);
        setCurrentPage(urlParams.page);

        // Clean up invalid URL parameters
        const currentDateRange = searchParams.get('dateRange');
        if (currentDateRange === '[object Object]' || (currentDateRange && currentDateRange.includes('[object Object]'))) {
            updateURLParams({ dateRange: null });
        }
    }, [searchParams]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        updateURLParams({ page });
        
        // Fetch data with the new page
        const urlParams = getURLParams();
        const commodityQuery = buildCommodityQuery(urlParams.commodities);
        const startDate = urlParams.dateRange?.start || "";
        const endDate = urlParams.dateRange?.end || "";
        const queryString = `username=${urlParams.search}&start_date=${startDate}&end_date=${endDate}&state=${urlParams.state}&user_type=${urlParams.userType}&min_farm_size=${urlParams.farmArea.split("-")[0]}&max_farm_size=${urlParams.farmArea.split("-")[1]}&${commodityQuery}&sort_by=${urlParams.sortBy}&sort_order=${urlParams.sortOrder}&page=${page}`;
        fetchData(queryString);
    };

    const buildCommodityQuery = (commodities) => {
        if (!commodities || commodities.length === 0) return "";

        if (commodities.includes("no_commodities")) {
            const actualCommodities = commodities.filter(id => id !== "no_commodities");
            if (actualCommodities.length > 0) {
                return `commodities=${actualCommodities.join(",")}&include_no_commodities=true`;
            } else {
                return `include_no_commodities=true`;
            }
        } else {
            return `commodities=${commodities.join(",")}`;
        }
    };

    const handleSearch = (query) => {
        setSearchText(query);
        updateURLParams({ search: query, page: 1 });
        const commodityQuery = buildCommodityQuery(filteredCommodities);
        fetchData(`username=${query}&start_date=${filterDate.start || ""}&end_date=${filterDate.end || ""}&state=${filteredState}&user_type=${filteredUserType}&min_farm_size=${filteredFarmArea.split("-")[0]}&max_farm_size=${filteredFarmArea.split("-")[1]}&${commodityQuery}&sort_by=${selectedSort}&sort_order=${selectedSortDir}&page=1`);
    };

    const handleDateChange = (value) => {
        setFilterDate(value);
        updateURLParams({ dateRange: value, page: 1 });
        const commodityQuery = buildCommodityQuery(filteredCommodities);
        fetchData(`username=${searchText}&start_date=${value.start || ""}&end_date=${value.end || ""}&state=${filteredState}&user_type=${filteredUserType}&min_farm_size=${filteredFarmArea.split("-")[0]}&max_farm_size=${filteredFarmArea.split("-")[1]}&${commodityQuery}&sort_by=${selectedSort}&sort_order=${selectedSortDir}&page=1`);
    };

    const handleStateChange = (value) => {
        setFilteredState(value);
        updateURLParams({ state: value, page: 1 });
        const commodityQuery = buildCommodityQuery(filteredCommodities);
        fetchData(`username=${searchText}&start_date=${filterDate.start || ""}&end_date=${filterDate.end || ""}&state=${value}&user_type=${filteredUserType}&min_farm_size=${filteredFarmArea.split("-")[0]}&max_farm_size=${filteredFarmArea.split("-")[1]}&${commodityQuery}&sort_by=${selectedSort}&sort_order=${selectedSortDir}&page=1`);
    };

    const handleUserTypeChange = (value) => {
        setFilteredUserType(value);
        updateURLParams({ userType: value, page: 1 });
        const commodityQuery = buildCommodityQuery(filteredCommodities);
        fetchData(`username=${searchText}&start_date=${filterDate.start || ""}&end_date=${filterDate.end || ""}&state=${filteredState}&user_type=${value}&min_farm_size=${filteredFarmArea.split("-")[0]}&max_farm_size=${filteredFarmArea.split("-")[1]}&${commodityQuery}&sort_by=${selectedSort}&sort_order=${selectedSortDir}&page=1`);
    };

    const handleFarmAreaChange = (value) => {
        setFilteredFarmArea(value);
        updateURLParams({ farmArea: value, page: 1 });
        const commodityQuery = buildCommodityQuery(filteredCommodities);
        fetchData(`username=${searchText}&start_date=${filterDate.start || ""}&end_date=${filterDate.end || ""}&state=${filteredState}&user_type=${filteredUserType}&min_farm_size=${value.split("-")[0]}&max_farm_size=${value.split("-")[1]}&${commodityQuery}&sort_by=${selectedSort}&sort_order=${selectedSortDir}&page=1`);
    };

    const handleCommodityChange = (value) => {
        setFilteredCommodities(value);
        updateURLParams({ commodities: value, page: 1 });
        const commodityQuery = buildCommodityQuery(value);
        fetchData(`username=${searchText}&start_date=${filterDate.start || ""}&end_date=${filterDate.end || ""}&state=${filteredState}&user_type=${filteredUserType}&min_farm_size=${filteredFarmArea.split("-")[0]}&max_farm_size=${filteredFarmArea.split("-")[1]}&${commodityQuery}&sort_by=${selectedSort}&sort_order=${selectedSortDir}&page=1`);
    };

    const resetFilters = () => {
        setSearchText("");
        setFilterDate({ start: null, end: null });
        setFilteredUserType("");
        setFilteredState("");
        setFilteredFarmArea("-");
        setFilteredCommodities([]);
        setSelectedSort("");
        setSelectedSortDir("");
        setCurrentPage(1);

        // Clear all URL parameters
        setSearchParams({}, { replace: true });
        fetchData("");
    };

    const fetchData = async (query) => {
        setLoading(true);
        try {
            let res = await getUsers(query);
            if (res.status === 200) {
                const data = res.data;
                setData(data);
                setCardData(data.results.stats);
            } else {
                toast.error("Unable to fetch Data");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Only fetch data if we have URL parameters or if it's the initial load
        const urlParams = getURLParams();
        const hasParams = Object.values(urlParams).some(value =>
            value !== "" && value !== null && value !== undefined &&
            !(Array.isArray(value) && value.length === 0) &&
            !(typeof value === 'object' && value.start === null && value.end === null)
        );

        if (hasParams) {
            const commodityQuery = buildCommodityQuery(urlParams.commodities);
            // Safely handle date range
            const startDate = urlParams.dateRange?.start || "";
            const endDate = urlParams.dateRange?.end || "";
            const queryString = `username=${urlParams.search}&start_date=${startDate}&end_date=${endDate}&state=${urlParams.state}&user_type=${urlParams.userType}&min_farm_size=${urlParams.farmArea.split("-")[0]}&max_farm_size=${urlParams.farmArea.split("-")[1]}&${commodityQuery}&sort_by=${urlParams.sortBy}&sort_order=${urlParams.sortOrder}&page=${urlParams.page}`;
            fetchData(queryString);
        } else {
            fetchData("");
        }
    }, [searchParams]);

    const openModal = (item) => {
        setOpeningItem(item);
        setIsOpen(true);
    };

    const closeModal = () => {
        setIsOpen(false);
        setOpeningItem({});
    };

    return (
        <div className="space-y-3">
            {/* Compact Header */}
            <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Breadcrumbs items={[
                            { name: 'Home', link: '/dashboard' },
                            { name: `${userType}` },
                        ]} />
                        <div className="h-4 w-px bg-gray-300"></div>
                        <h1 className="text-lg font-semibold text-gray-800">Farmers Management</h1>
                    </div>
                    <Link to="/farmers/add">
                        <Button
                            type="button"
                            className="bg-primary hover:bg-primary/90 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1.5"
                        >
                            <BiUserPlus size={16} />
                            Add User
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Compact Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {cardData.map((card, index) => (
                    <div
                        key={index}
                        className="bg-gradient-to-br from-primary/10 to-white border border-primary/20 shadow-sm rounded-xl p-4 flex flex-col items-start hover:shadow-md transition-shadow duration-200"
                    >
                        <div className="flex items-center gap-2 w-full">
                            {card.icon && (
                                <span className="text-primary text-lg">
                                    {card.icon}
                                </span>
                            )}
                            <span className="text-xs font-medium text-gray-600 truncate">{card.title}</span>
                        </div>
                        <div className="text-2xl font-extrabold text-primary mt-2">{card.value}</div>
                        {card.subtitle && (
                            <div className="text-xs text-gray-400 mt-1">{card.subtitle}</div>
                        )}
                    </div>
                ))}
            </div>

            {/* Compact Filters */}
            {/* Enhanced Filters Section */}
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200 rounded-xl shadow-sm p-4">
                {/* Header with Icon */}
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-slate-800">Search & Filters</h3>
                        <p className="text-xs text-slate-600">Refine your search with multiple criteria</p>
                    </div>
                    {/* Active Filter Count */}
                    {(searchText.length !== 0 || filterDate.start !== null || filterDate.end !== null || filteredState.length !== 0 || filteredCommodities.length !== 0 || filteredFarmArea !== "-" || selectedSort !== "" || selectedSortDir !== "") && (
                        <div className="ml-auto flex items-center gap-2">
                            <div className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                {[
                                    searchText.length !== 0,
                                    filterDate.start !== null || filterDate.end !== null,
                                    filteredState.length !== 0,
                                    filteredCommodities.length !== 0,
                                    filteredFarmArea !== "-",
                                    selectedSort !== "" || selectedSortDir !== ""
                                ].filter(Boolean).length} active
                            </div>
                            <button
                                onClick={resetFilters}
                                className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center gap-1.5 text-xs font-medium shadow-sm"
                                title="Reset All Filters"
                            >
                                <BiRefresh size={14} />
                                Reset All
                            </button>
                        </div>
                    )}
                </div>

                {/* Primary Search Row */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 mb-3">
                    {/* Search Bar */}
                    <div className="lg:col-span-4">
                        <label className="block text-xs font-medium text-slate-700 mb-1">Search</label>
                        <SearchBar
                            placeholder="Search by Number or Name..."
                            onSearch={handleSearch}
                            value={searchText}
                            className="h-9 w-full"
                        />
                    </div>

                    {/* User Type */}
                    <div className="lg:col-span-2">
                        <label className="block text-xs font-medium text-slate-700 mb-1">User Type</label>
                        <Dropdown
                            placeholder="Select Type"
                            options={[
                                { label: "All Types", value: "" },
                                { label: "👨‍🌾 Farmer", value: "farmer" },
                                { label: "👤 Other", value: "other" },
                            ]}
                            onChange={handleUserTypeChange}
                            value={filteredUserType}
                            needSearch={false}
                            className="h-9 text-sm w-full"
                        />
                    </div>

                    {/* State */}
                    <div className="lg:col-span-2">
                        <label className="block text-xs font-medium text-slate-700 mb-1">State</label>
                        <Dropdown
                            placeholder="Select State"
                            options={[
                                { label: "All States", value: "" },
                                ...statesOfIndia.map((e) => ({ label: e, value: e }))
                            ]}
                            onChange={handleStateChange}
                            value={filteredState}
                            needSearch={true}
                            className="h-9 text-sm w-full"
                        />
                    </div>

                    {/* Date Range */}
                    <div className="lg:col-span-4">
                        <label className="block text-xs font-medium text-slate-700 mb-1">Date Range</label>
                        <DateRangePicker
                            value={filterDate}
                            onChange={handleDateChange}
                            placeholder="Select Date Range"
                            className="h-9 text-sm w-full"
                        />
                    </div>
                </div>

                {/* Secondary Filters Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {/* Farm Area */}
                    <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Farm Area</label>
                        <Dropdown
                            placeholder="Select Farm Size"
                            options={[
                                { label: "🏠 All Sizes", value: "-" },
                                { label: "🌱 0.1-5 Acres (Small)", value: "0.1-5" },
                                { label: "🌾 5-10 Acres (Medium)", value: "5-10" },
                                { label: "🚜 10-20 Acres (Large)", value: "10-20" },
                                { label: "🏭 20-50 Acres (Very Large)", value: "20-50" },
                                { label: "🌍 50+ Acres (Enterprise)", value: "50-1000" },
                            ]}
                            onChange={handleFarmAreaChange}
                            value={filteredFarmArea}
                            needSearch={false}
                            className="h-9 text-sm w-full"
                        />
                    </div>

                    {/* Commodities */}
                    <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Commodities</label>
                        <MultiSelectDropdown
                            name="commodities"
                            options={[
                                { label: "🚫 No Commodities", value: "no_commodities" },
                                ...commodities.map((e) => ({ label: `🌾 ${e.name}`, value: e.id }))
                            ]}
                            placeholder="Select Commodities..."
                            value={filteredCommodities}
                            onChange={handleCommodityChange}
                            needSearch
                            className="h-9 text-sm w-full"
                        />
                    </div>
                </div>
            </div>

            {/* Compact Table Container */}
            <div className="bg-white border border-gray-200 rounded-lg">
                {loading ? (
                    <div className="flex justify-center py-8">
                        <CustomLoading size={60} />
                    </div>
                ) : (
                    <div>
                        {data.count > 0 && (
                            <Pagination
                                currentPage={currentPage}
                                pageSize={pageSize}
                                totalEntries={data?.count}
                                onPageChange={handlePageChange}
                                data={data}
                                setData={setData}
                                compact
                            />
                        )}

                        <div className="p-3">
                            <Table
                                headers={headers}
                                onFilterChange={handleTableSort}
                                currentSort={selectedSort}
                                currentSortDir={selectedSortDir}
                                rows={data?.results?.records?.map((item, index) => ({
                                    id: <span className='cursor-pointer hover:text-primary text-sm font-medium' onClick={() => openModal(item)}>{item.id}</span>,
                                    name: <span className='cursor-pointer hover:text-primary max-w-32 truncate text-sm font-medium block' title={`${item.first_name} ${item.last_name}`} onClick={() => openModal(item)}>{item.first_name} {item.last_name}</span>,
                                    user_type: <span className='cursor-pointer hover:text-primary text-sm' onClick={() => openModal(item)}>{item.segmentations.length > 0 ? item.segmentations[0].seg_type.toUpperCase() : "-"}</span>,
                                    mobile_number: <span className='cursor-pointer hover:text-primary text-sm' onClick={() => openModal(item)}>{item.mobile_number}</span>,
                                    total_purchased_amount: <span className='cursor-pointer hover:text-primary text-sm' onClick={() => openModal(item)}>₹{item.total_purchased_amount}</span>,
                                    commodities: <div className="grid grid-cols-2 gap-1">
                                        {(item?.commodities ?? []).map((commodity, index) => (
                                            <div title={commodity.name} className="border cursor-help border-primary rounded-md px-2 py-1 text-xs flex flex-row gap-1 items-center">
                                                <img src={commodity.image_url} alt={commodity.name} className='w-3 h-3' />
                                                <p className='text-[0.5rem] truncate'>
                                                    {commodity.name}
                                                </p>
                                            </div>
                                        ))}
                                    </div>,
                                    state: <span className='cursor-pointer hover:text-primary text-sm' onClick={() => openModal(item)}>{item.state}</span>,
                                    total_farm_size_acres: <span className='cursor-pointer hover:text-primary text-sm' onClick={() => openModal(item)}>{item.total_farm_size_acres} Acers</span>,
                                    total_devices: <span className={`cursor-pointer hover:text-primary text-sm ${item.total_devices !== 0 ? "text-green-600 font-semibold" : "text-gray-500"}`} onClick={() => openModal(item)}>{item.total_devices}</span>,
                                    total_registered_farms: <span className={`cursor-pointer hover:text-primary text-sm ${item.total_registered_farms !== 0 ? "text-green-600 font-semibold" : "text-gray-500"}`} onClick={() => openModal(item)}>{item.total_registered_farms}</span>,
                                    total_satellite_registered_farms: <span className={`cursor-pointer hover:text-primary text-sm ${item.total_satellite_registered_farms !== 0 ? "text-green-600 font-semibold" : "text-gray-500"}`} onClick={() => openModal(item)}>{item.total_satellite_registered_farms}</span>,
                                    feedback: <div className="flex flex-row gap-1 items-center">
                                        <History className='cursor-pointer hover:text-primary text-sm' onClick={() => { setFeedbackModalIsOpen(true); setFeedbackItem(item); }} />
                                        <span className='text-xs text-gray-500'>{item.segmentations.length > 0 ? item.segmentations[0].feedback : ""}</span>
                                    </div>,
                                }))}
                            />
                        </div>
                        {data.count > 0 && (
                            <Pagination
                                currentPage={currentPage}
                                pageSize={pageSize}
                                totalEntries={data?.count}
                                onPageChange={handlePageChange}
                                data={data}
                                setData={setData}
                                compact
                            />
                        )}
                    </div>
                )}
            </div>

            <ShowModal
                contentLabel={"User Details"}
                isOpen={modalIsOpen}
                modalWidth="80%"
                isSidebar={true}
                onRequestClose={closeModal}
                componentToPass={<UserDetailsPop
                    item={openingItem}
                    onUpdateAny={() => {
                        const commodityQuery = buildCommodityQuery(filteredCommodities);
                        fetchData(`username=${searchText}&start_date=${filterDate.start || ""}&end_date=${filterDate.end || ""}&state=${filteredState}&user_type=${filteredUserType}&min_farm_size=${filteredFarmArea.split("-")[0]}&max_farm_size=${filteredFarmArea.split("-")[1]}&${commodityQuery}&sort_by=${selectedSort}&sort_order=${selectedSortDir}&page=${currentPage}`);
                        closeModal();
                    }}
                />}
            />

            <ShowModal
                contentLabel={"User Feedback"}
                isOpen={feedbackModalIsOpen}
                modalWidth="80%"
                isSidebar={true}
                onRequestClose={() => { setFeedbackModalIsOpen(false); setFeedbackItem({}); }}
                componentToPass={<UserFeedbackPop
                    item={feedbackItem}
                    onUpdateAny={() => {
                        const commodityQuery = buildCommodityQuery(filteredCommodities);
                        fetchData(`username=${searchText}&start_date=${filterDate.start || ""}&end_date=${filterDate.end || ""}&state=${filteredState}&user_type=${filteredUserType}&min_farm_size=${filteredFarmArea.split("-")[0]}&max_farm_size=${filteredFarmArea.split("-")[1]}&${commodityQuery}&sort_by=${selectedSort}&sort_order=${selectedSortDir}&page=${currentPage}`);
                        setFeedbackModalIsOpen(false);
                        setFeedbackItem({});
                    }}
                />}
            />
        </div>
    );
}

export default Farmers;